import { useEffect } from 'react';

import { getProductUrl, type ProductHit } from '@libs/algolia';
import { algoliaAutofillTrack, initAlgoliaTracking } from '@libs/analytics';

import type { AutocompleteItem, PanelListProps } from '../types';
import { getSectionTitle } from '../utils';
import { PanelItem } from './PanelItem';

export function PanelList({ autocomplete, items, source, highlightedIndex, onKeyDown, query }: PanelListProps) {
    const autocompleteClasses: Record<string, string> = {
        recentSearchesPlugin: 'recent-searches',
        querySuggestionsPlugin: 'query-suggestions',
        productSuggestionsPlugin: 'product-suggestions',
    };
    useEffect(() => {
        void initAlgoliaTracking();
    }, []);

    const sectionTitle =
        source.sourceId === 'querySuggestionsPlugin' && query !== '' ? '' : getSectionTitle(source.sourceId);

    const handleItemClick = (item: AutocompleteItem, searchAssistTerm: string) => {
        const itemName = item.name ?? (item.label as string) ?? item.objectID;
        window.dispatchEvent(
            new CustomEvent('getAssistedSearchData', {
                detail: {
                    searchTerm: itemName,
                    searchAssistTerm,
                    searchAssistName: sectionTitle.toLowerCase(),
                },
            })
        );
    };

    return (
        <section className="autocomplete-source">
            {sectionTitle !== '' && (
                <div className="autocomplete-source-header" data-testid="popular-searches">
                    {sectionTitle}
                </div>
            )}
            <ul className="autocomplete-list" {...autocomplete.getListProps()}>
                {items.map((item, index) => {
                    const itemProps = autocomplete.getItemProps({
                        item,
                        source,
                    });

                    return (
                        <PanelItem
                            className={`autocomplete-${autocompleteClasses[source.sourceId]}`}
                            onKeyDown={onKeyDown}
                            item={item}
                            focus={index === highlightedIndex}
                            key={item.objectID ?? item.label}
                            sourceId={source.sourceId}
                            {...itemProps}
                            onClick={e => {
                                const searchInput = document.getElementById(
                                    'autocmplete-search-box'
                                ) as HTMLInputElement;

                                const searchAssistTerm: string = searchInput?.value || '';

                                handleItemClick(item, searchAssistTerm);
                                const productUrl = getProductUrl(item as ProductHit);

                                if (productUrl && item.__autocomplete_queryID) {
                                    algoliaAutofillTrack(item);
                                }
                                return itemProps.onClick(e);
                            }}
                        />
                    );
                })}
            </ul>
        </section>
    );
}
